import React, { useMemo, useEffect } from "react"
import PropTypes from "prop-types"
import {
  TextField,
  MenuItem,
  CircularProgress,
  InputAdornment,
} from "@mui/material"

import { useSeries } from "./hooks/useSeries"
import { buildSerieNextNumber } from "./buildSeriePreview"

const SerieSelector = ({
  organizationId,
  serieType,
  mode,
  value,
  onChange,
  currentNumber,
  label,
  hasError = false,
}) => {
  const { data: allSeries = [], isLoading } = useSeries(
    organizationId,
    serieType,
    { enabled: mode === "create" },
  )

  const activeSeries = useMemo(
    () => allSeries.filter(s => !s.archived && !s.exhausted),
    [allSeries],
  )

  const defaultSerie = useMemo(
    () => activeSeries.find(s => s.is_default) || null,
    [activeSeries],
  )

  useEffect(() => {
    if (mode !== "create") return
    if (value) return
    if (activeSeries.length === 0) return
    const target = defaultSerie?.SK || activeSeries[0].SK
    if (target) onChange(target)
  }, [mode, value, activeSeries, defaultSerie, onChange])

  if (mode === "view") {
    return (
      <TextField
        label={label}
        variant="outlined"
        fullWidth
        type="text"
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: true,
          style: { background: "#f5f5f5" },
        }}
        value={currentNumber || ""}
      />
    )
  }

  if (isLoading) {
    return (
      <TextField
        label={label}
        variant="outlined"
        fullWidth
        type="text"
        disabled
        sx={{ mb: 2 }}
        value=""
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <CircularProgress size={18} color="inherit" />
            </InputAdornment>
          ),
        }}
      />
    )
  }

  if (activeSeries.length === 0) {
    return (
      <TextField
        label={label}
        variant="outlined"
        fullWidth
        type="text"
        disabled
        sx={{ mb: 2 }}
        value=""
      />
    )
  }

  if (activeSeries.length === 1) {
    const only = activeSeries[0]
    return (
      <TextField
        label={label}
        variant="outlined"
        fullWidth
        type="text"
        sx={{ mb: 2 }}
        InputProps={{
          readOnly: true,
          style: { background: "#f5f5f5" },
        }}
        value={buildSerieNextNumber(only)}
      />
    )
  }

  return (
    <TextField
      select
      label={label}
      variant="outlined"
      fullWidth
      sx={{ mb: 2 }}
      value={value || ""}
      onChange={e => onChange(e.target.value)}
      error={hasError && !value}
    >
      {activeSeries.map(s => (
        <MenuItem key={s.SK} value={s.SK}>
          {buildSerieNextNumber(s)}
        </MenuItem>
      ))}
    </TextField>
  )
}

SerieSelector.propTypes = {
  organizationId: PropTypes.string.isRequired,
  serieType: PropTypes.oneOf([
    "invoices",
    "correctives",
    "proformas",
    "purchase_orders",
  ]).isRequired,
  mode: PropTypes.oneOf(["create", "view"]).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func,
  currentNumber: PropTypes.string,
  label: PropTypes.string.isRequired,
  hasError: PropTypes.bool,
}

export default SerieSelector
